export const INITIAL_DNP3_DEVICE_FORM = Object.freeze({
  name: "",
  host: "",
  port: "20000",
  masterAddress: "1",
  outstationAddress: "10",
  timeoutMs: "2000",
  reconnectMinMs: "250",
  reconnectMaxMs: "30000",
  integrityPollIntervalMs: "5000",
  enabled: true,
});

const parseInteger = (value) => {
  if (typeof value === "number") return value;
  const text = String(value ?? "").trim();
  return text === "" ? Number.NaN : Number(text);
};

const requireIntegerInRange = (value, label, min, max) => {
  const number = parseInteger(value);
  if (!Number.isInteger(number) || number < min || number > max) {
    return `${label} must be a whole number from ${min} to ${max}.`;
  }
  return "";
};

const IPV6_ZONE_RE = /^[A-Za-z0-9._-]{1,64}$/;

const isIPv4Address = (value) => {
  const parts = value.split(".");
  if (parts.length !== 4) return false;
  return parts.every((part) => {
    if (!/^\d+$/.test(part) || (part.length > 1 && part.startsWith("0"))) return false;
    const octet = Number(part);
    return octet >= 0 && octet <= 255;
  });
};

const looksLikeDottedIPv4 = (value) => {
  const parts = value.split(".");
  return parts.length === 4 && parts.every((part) => /^\d+$/.test(part));
};

const isIPv6Address = (value) => {
  let address = value;
  const percent = address.indexOf("%");
  if (percent >= 0) {
    if (percent !== address.lastIndexOf("%") || !IPV6_ZONE_RE.test(address.slice(percent + 1))) return false;
    address = address.slice(0, percent);
  }
  if (!address.includes(":")) return false;

  const compression = address.indexOf("::");
  if (compression >= 0 && compression !== address.lastIndexOf("::")) return false;
  const compressed = compression >= 0;
  const left = compressed ? address.slice(0, compression) : address;
  const right = compressed ? address.slice(compression + 2) : "";
  const leftParts = left ? left.split(":") : [];
  const rightParts = right ? right.split(":") : [];
  const parts = [...leftParts, ...rightParts];
  if (parts.some((part) => part === "")) return false;

  let units = 0;
  for (let index = 0; index < parts.length; index += 1) {
    const part = parts[index];
    if (part.includes(".")) {
      // An embedded IPv4 address occupies the final two 16-bit words and may
      // appear only at the end of the complete IPv6 address.
      if (index !== parts.length - 1 || (compressed && rightParts.length === 0) || !isIPv4Address(part)) return false;
      units += 2;
      continue;
    }
    if (!/^[0-9A-Fa-f]{1,4}$/.test(part)) return false;
    units += 1;
  }
  return compressed ? units < 8 : units === 8;
};

const isDNSHost = (value) => {
  const absolute = value.endsWith(".") ? value.slice(0, -1) : value;
  if (!absolute || absolute.length > 253) return false;
  return absolute.split(".").every((label) => (
    label.length >= 1 &&
    label.length <= 63 &&
    /^[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$/.test(label)
  ));
};

export function validateDnp3Host(value) {
  const host = String(value || "").trim();
  if (!host) return "Host is required.";
  if (host.includes("://") || host.startsWith("//")) {
    return "Host must not include a URL scheme. Enter only a DNS name or IP address.";
  }
  if (host.includes("[") || host.includes("]")) {
    return "Host must be unbracketed. Enter IPv6 like fe80::1 or fe80::1%eth0.";
  }

  const percent = host.indexOf("%");
  if (percent >= 0) {
    if (percent !== host.lastIndexOf("%") || percent === 0 || percent === host.length - 1) {
      return "Host has an invalid IPv6 zone.";
    }
    if (!IPV6_ZONE_RE.test(host.slice(percent + 1))) {
      return "IPv6 zone may contain only letters, numbers, '.', '_', or '-'.";
    }
    if (!isIPv6Address(host)) {
      return "IPv6 zones are valid only on an unbracketed IPv6 address.";
    }
    return "";
  }

  if (isIPv4Address(host) || isIPv6Address(host)) return "";
  if ((host.match(/:/g) || []).length === 1) {
    return "Host must not include a port. Use the port field.";
  }
  if (host.includes(":")) return "Host must be a valid unbracketed IPv6 address.";
  if (looksLikeDottedIPv4(host) || !isDNSHost(host)) {
    return "Host must be a valid DNS name, IPv4 address, or unbracketed IPv6 address.";
  }
  return "";
}

export function defaultDnp3DeviceForm() {
  return { ...INITIAL_DNP3_DEVICE_FORM };
}

export function dnp3DeviceFormFromRecord(device) {
  if (!device) return defaultDnp3DeviceForm();
  return {
    name: device.name || "",
    host: device.host || "",
    port: String(device.port ?? 20000),
    masterAddress: String(device.master_address ?? 1),
    outstationAddress: String(device.outstation_address ?? 10),
    timeoutMs: String(device.timeout_ms ?? 2000),
    reconnectMinMs: String(device.reconnect_min_ms ?? 250),
    reconnectMaxMs: String(device.reconnect_max_ms ?? 30000),
    integrityPollIntervalMs: String(device.integrity_poll_interval_ms ?? 5000),
    enabled: device.enabled !== false,
  };
}

// Mirrors the backend's Device.Validate() in core/drivers/dnp3/device.go: the
// integrity poll floor there (500 ms) is stricter than the SQL CHECK
// (50 ms), so this validates against the floor the API actually enforces.
export function validateDnp3DeviceForm(form) {
  if (!String(form?.name || "").trim()) return "Name is required.";

  const hostError = validateDnp3Host(form?.host);
  if (hostError) return hostError;

  const checks = [
    requireIntegerInRange(form.port, "Port", 1, 65535),
    requireIntegerInRange(form.masterAddress, "Master address", 0, 65535),
    requireIntegerInRange(form.outstationAddress, "Outstation address", 0, 65535),
    requireIntegerInRange(form.timeoutMs, "Request timeout", 50, 60000),
    requireIntegerInRange(form.reconnectMinMs, "Minimum reconnect delay", 50, 60000),
    requireIntegerInRange(form.reconnectMaxMs, "Maximum reconnect delay", 50, 300000),
    requireIntegerInRange(
      form.integrityPollIntervalMs,
      "Integrity poll interval",
      500,
      3600000
    ),
  ];
  const invalid = checks.find(Boolean);
  if (invalid) return invalid;

  if (parseInteger(form.reconnectMaxMs) < parseInteger(form.reconnectMinMs)) {
    return "Maximum reconnect delay must be at least the minimum reconnect delay.";
  }
  if (parseInteger(form.masterAddress) === parseInteger(form.outstationAddress)) {
    return "Master address and outstation address must differ.";
  }
  return "";
}

export function buildDnp3DevicePayload(form) {
  return {
    name: String(form.name).trim(),
    host: String(form.host).trim(),
    port: parseInteger(form.port),
    master_address: parseInteger(form.masterAddress),
    outstation_address: parseInteger(form.outstationAddress),
    timeout_ms: parseInteger(form.timeoutMs),
    reconnect_min_ms: parseInteger(form.reconnectMinMs),
    reconnect_max_ms: parseInteger(form.reconnectMaxMs),
    integrity_poll_interval_ms: parseInteger(form.integrityPollIntervalMs),
    enabled: !!form.enabled,
  };
}

export function deriveDnp3DeviceStatus(device, state, stateError) {
  if (device?.enabled === false) return { key: "disabled", label: "Disabled" };

  const backendStatus = String(state?.status || "").trim().toLowerCase();
  const labels = {
    online: "Online",
    degraded: "Degraded",
    offline: "Offline",
    disabled: "Disabled",
  };
  if (labels[backendStatus]) {
    return {
      key: backendStatus,
      label: stateError ? `${labels[backendStatus]} (last known)` : labels[backendStatus],
    };
  }
  if (stateError) return { key: "unavailable", label: "Unavailable" };
  return { key: "checking", label: "Checking" };
}
